import { Suspense, useCallback, useMemo, useState } from 'react';

import {
  Box,
  Group,
  Popover,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';

import { IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Avatar } from '../../../../../shared/components/UI/Avatar/Avatar';
import { Button } from '../../../../../shared/components/UI/Button/Button';
import { EmptyPlaceholder } from '../../../../../shared/components/UI/EmptyPlaceholder/EmptyPlaceholder';
import { Tooltip } from '../../../../../shared/components/UI/Tooltip/Tooltip';

import type { GetThreadsResponseDTO } from '../../../../../shared/api/magellan/assistant/dto';
import { useGetThreads } from '../hooks/assistant.hook';

export function AssistantHistory() {
  const { t } = useTranslation('bidResponse');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  function onToggleHistory() {
    setIsHistoryOpen(!isHistoryOpen);
  }

  return (
    <Popover
      position="bottom-end"
      withinPortal
      opened={isHistoryOpen}
      onChange={onToggleHistory}
    >
      <Popover.Target>
        <Button variant="outline" onClick={onToggleHistory}>
          {t('assistant.history.showButton')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown
        sx={theme => ({
          padding: 0,
          paddingTop: theme.spacing['04'],
          paddingBottom: theme.spacing['04'],
          borderRadius: 20,
        })}
      >
        <ScrollArea h={350}>
          <Suspense fallback={<HistoryContentLoader />}>
            <HistoryContent toggleHistory={onToggleHistory} />
          </Suspense>
        </ScrollArea>
      </Popover.Dropdown>
    </Popover>
  );
}

function HistoryContentLoader() {
  return (
    <Group spacing={'02'} w={270} px="04">
      <Skeleton height={20} width={100} />
      <Group w="100%" spacing={'02'}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} height={40} width="100%" />
        ))}
      </Group>
    </Group>
  );
}

interface HistoryContentProps {
  toggleHistory: (value: boolean) => void;
}

function HistoryContent({ toggleHistory }: HistoryContentProps) {
  const { t } = useTranslation('bidResponse');
  const { data: threads } = useGetThreads();

  const onHistoryItemClick = useCallback(() => {
    toggleHistory(false);
  }, [toggleHistory]);

  const content = useMemo(() => {
    if (!threads?.length) {
      return <EmptyPlaceholder title={t('assistant.history.emptyTitle')} />;
    }

    return threads.map(thread => (
      <ThreadItem
        key={thread.id}
        thread={thread}
        onItemClick={onHistoryItemClick}
      />
    ));
  }, [threads, t, onHistoryItemClick]);

  return (
    <Stack spacing={'02'} w={270} px="04">
      <Title order={6} color="primary.7">
        {t('assistant.history.title')}
      </Title>
      <Stack w="100%" spacing={'02'}>
        {content}
      </Stack>
    </Stack>
  );
}

interface ThreadItemProps {
  thread: GetThreadsResponseDTO[number];
  onItemClick: (threadId: number) => void;
}

function ThreadItem({ thread, onItemClick }: ThreadItemProps) {
  const theme = useMantineTheme();

  const to = `/response/assistant/${thread.id}`;

  const { initials, color, displayName } = useMemo(() => {
    const firstName = thread.owner?.firstName ?? '';
    const lastName = thread.owner?.lastName ?? '';
    const color = thread.owner?.avatarColor ?? '';
    return {
      initials: `${firstName[0]}${lastName[0]}`,
      color,
      displayName: `${firstName} ${lastName}`,
    };
  }, [thread]);

  const date = useMemo(() => {
    return dayjs(thread.createdAt).fromNow();
  }, [thread]);

  function handleClick() {
    onItemClick(thread.id);
  }

  return (
    <Link
      onClick={handleClick}
      to={to}
      style={{ textDecoration: 'none', width: '100%', overflow: 'hidden' }}
    >
      <Group
        w="100%"
        spacing={'01'}
        noWrap
        px="02"
        py="01"
        sx={{
          '&:hover': {
            backgroundColor: theme.colors.primary[1],
            borderRadius: 8,
          },
        }}
      >
        <Stack spacing={'00'} style={{ flex: 1, overflow: 'hidden' }}>
          <Tooltip content={thread.title}>
            <Text size={'sm'} color="gray.8" truncate>
              {thread.title}
            </Text>
          </Tooltip>
          <Group spacing={'01'} align="center">
            <Group
              spacing={'01'}
              align="center"
              noWrap
              style={{ overflow: 'hidden' }}
            >
              <Avatar color={color} size="sm">
                {initials}
              </Avatar>
              <Text variant="xs" c="gray.6" lineClamp={1} truncate>
                {displayName}
              </Text>
            </Group>
            <Box bg="#D9D9D9" h={4} w={4} style={{ borderRadius: 8 }} />
            <Text variant="xs" c="gray.6" lineClamp={1}>
              {date}
            </Text>
          </Group>
        </Stack>
        <IconChevronRight color={theme.colors.gray[6]} size={16} />
      </Group>
    </Link>
  );
}
